import { forwardRef, useEffect, useState } from "react";
import Modal, { CommonModalProps } from "..";
import { DbStorageInfos } from "../../../Core/Api/Types/db-storage-infos";
import {
  Stack,
  Button,
  Grid,
  FormControl,
  InputLabel,
  OutlinedInput,
  FormControlLabel,
  Switch,
  MenuItem,
  Select,
  Divider,
  Chip,
  Paper,
  Typography,
  Alert,
  AlertTitle,
} from "@mui/material";
import { StorageType } from "../../../Core/Api/Types/storage-type";
import FormSection from "../../Forms/FormSection/form-section";
import { StorageContext } from "../../../Core/Api/Types/storage-context";
import { StorageKind } from "../../../Core/Api/Types/storage-kind";
import { DbStorageBaseInfos, SettingPath } from "../../../Core/Api/Types/db-storage-base-infos";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { DbStorageFileDetectionSettingsInfos } from "../../../Core/Api/Types/db-storage-file-detection-settings-infos";
import { SearchOption } from "../../../Core/Api/Types/search-option";
import FormAutocomplete from "../../Forms/FormAutocomplete/form-autocomplete";
import { IsEmail } from "../../../Utils/helpers";
import { DbStorageSnsmInfos, DbStorageSnsmSyncSettingsInfos } from "../../../Core/Api/Types/db-storage-snsm-infos";
import { DbStorageFtpInfos } from "../../../Core/Api/Types/db-storage-ftp-infos";
import { DbStorageDivaDestinationMapInfos, DbStorageDivaInfos } from "../../../Core/Api/Types/db-storage-diva-infos";
import { AsperaServerSettings, DbStorageAsperaInfos } from "../../../Core/Api/Types/db-storage-aspera-infos";
import { DbStorageUncInfos } from "../../../Core/Api/Types/db-storage-unc-infos";
import { DbStorageAmazonS3Infos, S3StorageClass } from "../../../Core/Api/Types/db-storage-amazon-s3-infos";
import { StorageDivaMedia } from "../../../Core/Api/Types/storage-diva-media";
import { StorageSnsmApiMode } from "../../../Core/Api/Types/storage-snsm-api-mode";
import { StorageSnsmVersion } from "../../../Core/Api/Types/storage-snsm-version";
import { setupService } from "../../../Core/Api/services";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { StoragePathAliasType } from "../../../Core/Api/Types/storage-path-alias-type";
import { DbStoragePathAliasInfos } from "../../../Core/Api/Types/db-storage-path-alias-infos";
import { StorageFtpProtocol } from "../../../Core/Api/Types/storage-ftp-protocol";
import { useTranslation } from "react-i18next";

export type ModalStorageEditElementProps = {
  storage: DbStorageInfos;
  isCreation: boolean;
  listOfAllStorages: { id: number; name: string }[];
  feedbackData: (storage: DbStorageInfos) => void;
};

export type ModalStorageEditProps = CommonModalProps<ModalStorageEditElementProps>;

const ModalStorageEdit = forwardRef<any, ModalStorageEditProps>((props, ref) => {
  const { t } = useTranslation();
  const { isCreation, closeModal, listOfAllStorages, feedbackData } = props;
  const [storage, setStorage] = useState<DbStorageInfos>(props.storage);
  const enqueueSnackbar = useCustomSnackbar();

  const handleCancelClick = () => {
    closeModal();
  };

  const storageTohisType = (type, storage) => {
    switch (type) {
      case StorageType.AMAZONS3:
        return {
          ...storage,
          $type: "VDM.AirLab.Sdk.Core.Database.Types.DbStorageAmazonS3Infos, VDM.AirLab.Sdk.Core",
        } as DbStorageAmazonS3Infos;
      case StorageType.ASPERA:
        return {
          ...storage,
          $type: "VDM.AirLab.Sdk.Core.Database.Types.DbStorageAsperaInfos, VDM.AirLab.Sdk.Core",
        } as DbStorageAsperaInfos;
      case StorageType.DIVA:
        return {
          ...storage,
          $type: "VDM.AirLab.Sdk.Core.Database.Types.DbStorageDivaInfos, VDM.AirLab.Sdk.Core",
        } as DbStorageDivaInfos;
      case StorageType.SNSM:
        return {
          ...storage,
          $type: "VDM.AirLab.Sdk.Core.Database.Types.DbStorageSnsmInfos, VDM.AirLab.Sdk.Core",
        } as DbStorageSnsmInfos;
      case StorageType.FTP:
        return {
          ...storage,
          $type: "VDM.AirLab.Sdk.Core.Database.Types.DbStorageFtpInfos, VDM.AirLab.Sdk.Core",
        } as DbStorageFtpInfos;
      case StorageType.UNC:
        return {
          ...storage,
          $type: "VDM.AirLab.Sdk.Core.Database.Types.DbStorageUncInfos, VDM.AirLab.Sdk.Core",
        } as DbStorageUncInfos;
      default:
        return storage as DbStorageBaseInfos;
    }
  };

  const handleConfirmClick = () => {
    storage.storage = storageTohisType(storage.type, storage.storage);

    (isCreation ? setupService.CreateStorage(storage) : setupService.UpdateStorage(storage)).then((data) => {
      if (!data.success || !data.objectId) {
        enqueueSnackbar(data.message!, "error");
        return;
      }
      feedbackData({ ...storage, id: data.objectId });
      enqueueSnackbar(storage.name!, "success");
      closeModal();
    });
  };

  return (
    <Modal
      title={t("modal:title.storageAction", { action: isCreation ? t("common:creation") : t("common:edit") })}
      reference={ref}
      closeModal={handleCancelClick}
      width={800}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
          <Button variant="contained" color="primary" onClick={handleConfirmClick} disabled={false}>
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <FormSection title={t("common:generalInformations")} status="todo">
        <Grid container spacing={3} flexDirection={"row"}>
          <Grid item md={6}>
            <FormControl sx={{ width: "100%" }}>
              <InputLabel shrink htmlFor="name">
                {t("common:name")}
              </InputLabel>
              <OutlinedInput
                value={storage.name ?? ""}
                id="name"
                onChange={(e) => {
                  setStorage({ ...storage, name: e.target.value });
                }}
              />
            </FormControl>
          </Grid>
          <Grid item md={6}>
            <InputLabel sx={{ mb: 1 }}>{t("common:type")}</InputLabel>
            <Select
              value={storage?.type}
              sx={{ width: "100%" }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
              fullWidth
              onChange={(e) => {
                setStorage({
                  ...storage,
                  type: e.target.value ? StorageType[e.target.value] : null,
                  storage: storageTohisType(storage.type, {}),
                });
              }}
            >
              {Object.keys(StorageType).map((type) => (
                <MenuItem value={type} key={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item md={6}>
            <InputLabel sx={{ mb: 1 }}>{t("common:context")}</InputLabel>
            <Select
              value={storage?.context}
              sx={{ width: "100%" }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
              fullWidth
              onChange={(e) => {
                setStorage({ ...storage, context: e.target.value ? StorageContext[e.target.value] : null });
              }}
            >
              {Object.keys(StorageContext).map((type) => (
                <MenuItem value={type} key={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item md={6}>
            <InputLabel sx={{ mb: 1 }}>{t("common:kind")}</InputLabel>
            <Select
              value={storage?.kind}
              sx={{ width: "100%" }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
              fullWidth
              onChange={(e) => {
                setStorage({ ...storage, kind: e.target.value ? StorageKind[e.target.value] : null });
              }}
            >
              {Object.keys(StorageKind).map((type) => (
                <MenuItem value={type} key={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item md={6}>
            <InputLabel sx={{ mb: 1 }}>{t("common:objectTypesUpperCase")}</InputLabel>
            <Select
              value={storage?.objectTypes ?? []}
              sx={{ width: "100%" }}
              multiple
              MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
              fullWidth
              onChange={(e) => {
                const { value } = e.target;

                setStorage({
                  ...storage,
                  objectTypes: e.target.value
                    ? typeof value === "string"
                      ? value.split(",").map((x) => ObjectType[x])
                      : value
                    : null,
                });
              }}
            >
              {Object.keys(ObjectType).map((type) => (
                <MenuItem value={type} key={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item md={6}>
            <InputLabel htmlFor="MaxParallelTransfers" sx={{ mb: 1 }}>
              {t("field:label.maxParallelTransfers")}
            </InputLabel>
            <OutlinedInput
              id="MaxParallelTransfers"
              type={"number"}
              value={storage.storage?.maxParallelTransfers}
              fullWidth
              onChange={(e) => {
                setStorage({
                  ...storage,
                  storage: {
                    ...(storage.storage ?? ({} as DbStorageBaseInfos)),
                    maxParallelTransfers: parseInt(e.target.value),
                  },
                });
              }}
            />
          </Grid>
          <Grid item md={6}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  onChange={(e, checked) => {
                    setStorage({ ...storage, isilonLocalAccountCreationSupport: checked });
                  }}
                  checked={storage.isilonLocalAccountCreationSupport ?? false}
                />
              }
              labelPlacement="start"
              sx={{ ml: 0, justifyContent: "flex-end", width: "100%", ".MuiFormControlLabel-label": { flex: 1 } }}
              label={t("field:label.isilonLocalAccountCreationSupport")}
            />
          </Grid>
          <Grid item md={6}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  onChange={(e, checked) => {
                    setStorage({
                      ...storage,
                      storage: { ...(storage.storage ?? ({} as DbStorageBaseInfos)), hsmSupport: checked },
                    });
                  }}
                  checked={storage.storage?.hsmSupport ?? false}
                />
              }
              labelPlacement="start"
              sx={{ ml: 0, justifyContent: "flex-end", width: "100%", ".MuiFormControlLabel-label": { flex: 1 } }}
              label={t("field:label.hsmSupport")}
            />
          </Grid>
          <Grid item md={6}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  onChange={(e, checked) => {
                    setStorage({
                      ...storage,
                      storage: {
                        ...(storage.storage ?? ({} as DbStorageBaseInfos)),
                        excludeOfPrimaryArchiveSystem: checked,
                      },
                    });
                  }}
                  checked={storage.storage?.excludeOfPrimaryArchiveSystem ?? false}
                />
              }
              labelPlacement="start"
              sx={{ ml: 0, justifyContent: "flex-end", width: "100%", ".MuiFormControlLabel-label": { flex: 1 } }}
              label={t("field:label.excludeMeOfPrimaryArchiveSystem")}
            />
          </Grid>
          <Grid item md={6}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  onChange={(e, checked) => {
                    setStorage({ ...storage, allowDelete: checked });
                  }}
                  checked={storage.allowDelete ?? false}
                />
              }
              labelPlacement="start"
              sx={{ ml: 0, justifyContent: "flex-end", width: "100%", ".MuiFormControlLabel-label": { flex: 1 } }}
              label={t("field:label.allowDelete")}
            />
          </Grid>
          <Grid item md={6}>
            <FormAutocomplete
              label={t("field:label.emailContacts")}
              options={[]}
              freeSolo
              placeholder={t("field:placeholder.email")}
              value={storage?.emails ?? []}
              multiple={true}
              onChange={(e) => {
                if (e.length > 0 && !IsEmail(e[e.length - 1])) return;
                setStorage({
                  ...storage,
                  emails: e,
                });
              }}
              onReset={(v) => {
                // Non executed... strange, must use onChange
                // handleUpdateProductRoot(ProductField.Emails, []);
              }}
              getOptionLabel={(value: any) => value}
              isOptionEqualToValue={(option, value) => option === value}
              validationProps={{
                formId: "emails",
                formSectionId: "general",
                fieldId: "emails",
                required: false,
                initialState: false,
              }}
            />
          </Grid>
          <Grid item md={6}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  onChange={(e, checked) => {
                    setStorage({ ...storage, enabled: checked });
                  }}
                  checked={storage.enabled ?? false}
                />
              }
              labelPlacement="start"
              sx={{ ml: 0, justifyContent: "flex-end", width: "100%", ".MuiFormControlLabel-label": { flex: 1 } }}
              label={t("field:label.enableQuesionMark")}
            />
          </Grid>
        </Grid>
      </FormSection>
      {storage.type && storage.kind === StorageKind.Ingest && (
        <FormSection title={t("form:section.title.fileDetectionSettings")} status="todo" sx={{ mt: 2 }}>
          <Grid container spacing={3} flexDirection={"row"}>
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink htmlFor="scanningIntervalInMs">
                  {t("field:label.scanningIntervalInMs")}
                </InputLabel>
                <OutlinedInput
                  id="scanningIntervalInMs"
                  type={"number"}
                  value={storage?.storage?.fileDetectionSettings?.scanningIntervalInMs ?? 5000}
                  onChange={(e) => {
                    setStorage({
                      ...storage,
                      storage: {
                        ...(storage.storage ?? ({} as DbStorageBaseInfos)),
                        fileDetectionSettings: {
                          ...(storage?.storage?.fileDetectionSettings ?? new DbStorageFileDetectionSettingsInfos()),
                          scanningIntervalInMs: parseInt(e.target.value),
                        },
                      },
                    });
                  }}
                />
              </FormControl>
            </Grid>

            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink htmlFor="growingFileSleepingIntervalInMs">
                  {t("field:label.growingFileSleepingInternalInMs")}
                </InputLabel>
                <OutlinedInput
                  id="growingFileSleepingIntervalInMs"
                  type={"number"}
                  value={storage?.storage?.fileDetectionSettings?.growingFileSleepingIntervalInMs ?? 5000}
                  onChange={(e) => {
                    setStorage({
                      ...storage,
                      storage: {
                        ...(storage.storage ?? ({} as DbStorageBaseInfos)),
                        fileDetectionSettings: {
                          ...(storage?.storage?.fileDetectionSettings ?? new DbStorageFileDetectionSettingsInfos()),
                          growingFileSleepingIntervalInMs: parseInt(e.target.value),
                        },
                      },
                    });
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink htmlFor="growingFileCheckerLoopingNumber">
                  {t("field:label.growingFileCheckerLoopingNumber")}
                </InputLabel>
                <OutlinedInput
                  id="growingFileCheckerLoopingNumber"
                  type={"number"}
                  value={storage?.storage?.fileDetectionSettings?.growingFileCheckerLoopingNumber ?? 2}
                  onChange={(e) => {
                    setStorage({
                      ...storage,
                      storage: {
                        ...(storage.storage ?? ({} as DbStorageBaseInfos)),
                        fileDetectionSettings: {
                          ...(storage?.storage?.fileDetectionSettings ?? new DbStorageFileDetectionSettingsInfos()),
                          growingFileCheckerLoopingNumber: parseInt(e.target.value),
                        },
                      },
                    });
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink htmlFor="threadSize">
                  {t("field:label.threadSize")}
                </InputLabel>
                <OutlinedInput
                  id="threadSize"
                  type={"number"}
                  value={storage?.storage?.fileDetectionSettings?.threadSize ?? 5}
                  onChange={(e) => {
                    setStorage({
                      ...storage,
                      storage: {
                        ...(storage.storage ?? ({} as DbStorageBaseInfos)),
                        fileDetectionSettings: {
                          ...(storage?.storage?.fileDetectionSettings ?? new DbStorageFileDetectionSettingsInfos()),
                          threadSize: parseInt(e.target.value),
                        },
                      },
                    });
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel sx={{ mb: 1 }}>{t("field:label.searchOption")}</InputLabel>
                <Select
                  value={storage?.storage?.fileDetectionSettings?.searchOption}
                  sx={{ width: "100%" }}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                  onChange={(e) => {
                    setStorage({
                      ...storage,
                      storage: {
                        ...(storage.storage ?? ({} as DbStorageBaseInfos)),
                        fileDetectionSettings: {
                          ...(storage?.storage?.fileDetectionSettings ?? new DbStorageFileDetectionSettingsInfos()),
                          searchOption: e.target.value ? SearchOption[e.target.value] : null,
                        },
                      },
                    });
                  }}
                >
                  {Object.keys(SearchOption).map((type) => (
                    <MenuItem value={type} key={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item md={6}>
              <FormAutocomplete
                label={t("field:label.fileExtensionsUpperCase")}
                options={[]}
                freeSolo
                placeholder={t("field:placeholder.fileExtensionExample")}
                value={storage?.storage?.fileDetectionSettings?.fileExtensions ?? []}
                multiple={true}
                onChange={(e) => {
                  setStorage({
                    ...storage,
                    storage: {
                      ...(storage.storage ?? ({} as DbStorageBaseInfos)),
                      fileDetectionSettings: {
                        ...(storage?.storage?.fileDetectionSettings ?? new DbStorageFileDetectionSettingsInfos()),
                        fileExtensions: e,
                      },
                    },
                  });
                }}
                onReset={(v) => {
                  // Non executed... strange, must use onChange
                  // handleUpdateProductRoot(ProductField.Emails, []);
                }}
                getOptionLabel={(value: any) => value}
                isOptionEqualToValue={(option, value) => option === value}
                validationProps={{
                  formId: "fileExtensions",
                  formSectionId: "fileDetectionSettings",
                  fieldId: "fileExtensions",
                  required: false,
                  initialState: false,
                }}
              />
            </Grid>
          </Grid>
        </FormSection>
      )}
      {storage.type && storage.type == StorageType.UNC && (
        <FormSection title={t("form:section.title.uncSettings")} status="todo" sx={{ mb: 2, mt: 3 }}>
          <Grid container spacing={3} flexDirection={"row"}>
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink htmlFor="login">
                  {t("common:login")}
                </InputLabel>
                <OutlinedInput
                  value={(storage.storage as DbStorageUncInfos)?.user}
                  id="login"
                  onChange={(e) => {
                    setStorage({
                      ...storage,
                      storage: {
                        ...(storage.storage ?? ({} as DbStorageUncInfos)),
                        user: e.target.value,
                      },
                    });
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink htmlFor="password">
                  {t("common:password")}
                </InputLabel>
                <OutlinedInput
                  value={(storage.storage as DbStorageUncInfos)?.password}
                  id="password"
                  onChange={(e) => {
                    setStorage({
                      ...storage,
                      storage: {
                        ...(storage.storage ?? ({} as DbStorageUncInfos)),
                        password: e.target.value,
                      },
                    });
                  }}
                />
              </FormControl>
            </Grid>
            <SettingPathField
              linuxPath={(storage.storage?.path as SettingPath | null)?.linux}
              windowsPath={(storage.storage?.path as SettingPath | null)?.windows}
              onChange={(e) => {
                setStorage({
                  ...storage,
                  storage: {
                    ...(storage.storage ?? ({} as DbStorageUncInfos)),
                    path: e,
                  },
                });
              }}
            />
            <Grid item md={12}>
              <Paper sx={{ backgroundImage: "none", p: 2 }}>
                <Typography mb={2} variant="h4">
                  {t("modal:modalStorageEdit.body.pathAliases")}
                </Typography>
                <PathAliasField
                  pushToList={(element) => {
                    setStorage({
                      ...storage,
                      storage: {
                        ...((storage.storage as DbStorageUncInfos) ?? ({} as DbStorageUncInfos)),
                        pathAliases: [element, ...((storage.storage as DbStorageUncInfos)?.pathAliases ?? [])],
                      },
                    });
                  }}
                />
                <Grid item md={12}>
                  {(storage.storage as DbStorageUncInfos)?.pathAliases?.map((x) => (
                    <Chip
                      sx={{ ml: 1, mb: 0.3 }}
                      label={t("chip:label.storagePath", {
                        type: x.type,
                        linuxPath: x.path?.linux,
                        windowPath: x.path?.windows,
                      })}
                      onDelete={() =>
                        setStorage({
                          ...storage,
                          storage: {
                            ...((storage.storage as DbStorageUncInfos) ?? ({} as DbStorageUncInfos)),
                            pathAliases: [
                              ...((storage.storage as DbStorageUncInfos)?.pathAliases?.filter((y) => y !== x) ?? []),
                            ],
                          },
                        })
                      }
                    />
                  ))}
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </FormSection>
      )}
      {storage.type && storage.type == StorageType.ASPERA && (
        <FormSection title={t("form:section.title.asperaSettings")} status="todo" sx={{ mb: 2, mt: 3 }}>
          <Grid container spacing={3} flexDirection={"row"}>
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink htmlFor="host">
                  {t("common:host")}
                </InputLabel>
                <OutlinedInput
                  id="host"
                  value={(storage.storage as DbStorageAsperaInfos)?.settings?.host ?? ""}
                  onChange={(e) => {
                    setStorage({
                      ...storage,
                      storage: {
                        ...((storage.storage as DbStorageAsperaInfos) ?? ({} as DbStorageAsperaInfos)),
                        settings: {
                          ...((storage?.storage as DbStorageAsperaInfos | null)?.settings ??
                            ({} as AsperaServerSettings)),
                          host: e.target.value,
                        },
                      },
                    });
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink htmlFor="port">
                  {t("field:label.portTcp")}
                </InputLabel>
                <OutlinedInput
                  id="port"
                  type={"number"}
                  value={(storage.storage as DbStorageAsperaInfos)?.settings?.port ?? 33001}
                  onChange={(e) => {
                    setStorage({
                      ...storage,
                      storage: {
                        ...((storage.storage as DbStorageAsperaInfos) ?? ({} as DbStorageAsperaInfos)),
                        settings: {
                          ...((storage?.storage as DbStorageAsperaInfos | null)?.settings ??
                            ({} as AsperaServerSettings)),
                          port: parseInt(e.target.value),
                        },
                      },
                    });
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink htmlFor="portUdp">
                  {t("field:label.portUdp")}
                </InputLabel>
                <OutlinedInput
                  id="portUdp"
                  type={"number"}
                  value={(storage.storage as DbStorageAsperaInfos)?.settings?.portUdp ?? 33001}
                  onChange={(e) => {
                    setStorage({
                      ...storage,
                      storage: {
                        ...((storage.storage as DbStorageAsperaInfos) ?? ({} as DbStorageAsperaInfos)),
                        settings: {
                          ...((storage?.storage as DbStorageAsperaInfos | null)?.settings ??
                            ({} as AsperaServerSettings)),
                          portUdp: parseInt(e.target.value),
                        },
                      },
                    });
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink htmlFor="login">
                  {t("common:login")}
                </InputLabel>
                <OutlinedInput
                  id="login"
                  value={(storage.storage as DbStorageAsperaInfos)?.settings?.user ?? ""}
                  onChange={(e) => {
                    setStorage({
                      ...storage,
                      storage: {
                        ...((storage.storage as DbStorageAsperaInfos) ?? ({} as DbStorageAsperaInfos)),
                        settings: {
                          ...((storage?.storage as DbStorageAsperaInfos | null)?.settings ??
                            ({} as AsperaServerSettings)),
                          user: e.target.value,
                        },
                      },
                    });
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink htmlFor="password">
                  {t("common:password")}
                </InputLabel>
                <OutlinedInput
                  id="password"
                  value={(storage.storage as DbStorageAsperaInfos)?.settings?.password ?? ""}
                  onChange={(e) => {
                    setStorage({
                      ...storage,
                      storage: {
                        ...((storage.storage as DbStorageAsperaInfos) ?? ({} as DbStorageAsperaInfos)),
                        settings: {
                          ...((storage?.storage as DbStorageAsperaInfos | null)?.settings ??
                            ({} as AsperaServerSettings)),
                          password: e.target.value,
                        },
                      },
                    });
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink htmlFor="sshKeyFilePath">
                  {t("field:label.sshKeyFilePath")}
                </InputLabel>
                <OutlinedInput
                  id="sshKeyFilePath"
                  value={(storage.storage as DbStorageAsperaInfos)?.settings?.sshKeyFilePath ?? ""}
                  onChange={(e) => {
                    setStorage({
                      ...storage,
                      storage: {
                        ...((storage.storage as DbStorageAsperaInfos) ?? ({} as DbStorageAsperaInfos)),
                        settings: {
                          ...((storage?.storage as DbStorageAsperaInfos | null)?.settings ??
                            ({} as AsperaServerSettings)),
                          sshKeyFilePath: e.target.value,
                        },
                      },
                    });
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink htmlFor="path">
                  {t("field:label.docRootPath")}
                </InputLabel>
                <OutlinedInput
                  id="path"
                  value={(storage.storage as DbStorageAsperaInfos)?.settings?.path ?? ""}
                  onChange={(e) => {
                    setStorage({
                      ...storage,
                      storage: {
                        ...((storage.storage as DbStorageAsperaInfos) ?? ({} as DbStorageAsperaInfos)),
                        settings: {
                          ...((storage?.storage as DbStorageAsperaInfos | null)?.settings ??
                            ({} as AsperaServerSettings)),
                          path: e.target.value,
                        },
                      },
                    });
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item md={6}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    onChange={(e) => {
                      setStorage({
                        ...storage,
                        storage: {
                          ...((storage.storage as DbStorageAsperaInfos) ?? ({} as DbStorageAsperaInfos)),
                          settings: {
                            ...((storage?.storage as DbStorageAsperaInfos | null)?.settings ??
                              ({} as AsperaServerSettings)),
                            forceUseAbsolutePath: e.target.checked,
                          },
                        },
                      });
                    }}
                    checked={(storage.storage as DbStorageAsperaInfos)?.settings?.forceUseAbsolutePath ?? false}
                  />
                }
                labelPlacement="start"
                sx={{ ml: 0, justifyContent: "flex-end", width: "100%", ".MuiFormControlLabel-label": { flex: 1 } }}
                label={t("field:label.absolutePathSwitch")}
              />
            </Grid>
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink htmlFor="urlPrefix">
                  {t("field:label.forceServerUrlPrefix")}
                </InputLabel>
                <OutlinedInput
                  id="urlPrefix"
                  value={(storage.storage as DbStorageAsperaInfos)?.settings?.urlPrefix ?? ""}
                  onChange={(e) => {
                    setStorage({
                      ...storage,
                      storage: {
                        ...((storage.storage as DbStorageAsperaInfos) ?? ({} as DbStorageAsperaInfos)),
                        settings: {
                          ...((storage?.storage as DbStorageAsperaInfos | null)?.settings ??
                            ({} as AsperaServerSettings)),
                          urlPrefix: e.target.value,
                        },
                      },
                    });
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel sx={{ mb: 1 }}>{t("field:label.storageId")}</InputLabel>
                <Select
                  value={(storage.storage as DbStorageAsperaInfos)?.settings?.storageId}
                  sx={{ width: "100%" }}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                  onChange={(e) => {
                    setStorage({
                      ...storage,
                      storage: {
                        ...((storage.storage as DbStorageAsperaInfos) ?? ({} as DbStorageAsperaInfos)),
                        settings: {
                          ...((storage?.storage as DbStorageAsperaInfos | null)?.settings ??
                            ({} as AsperaServerSettings)),
                          storageId: e.target.value
                            ? typeof e.target.value == "string"
                              ? parseInt(e.target.value)
                              : e.target.value
                            : null,
                        },
                      },
                    });
                  }}
                >
                  {listOfAllStorages?.map((type, index) => (
                    <MenuItem value={type.id} key={index}>
                      #{type.id} | {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Alert severity="info" sx={{ mt: 4 }}>
            <AlertTitle sx={{ mb: 2 }}>
              Important: How to recognize an Aspera endpoint between "Node API", "Shares", "Faspex" and a classic
              "Server 2 Server"?
            </AlertTitle>
            - <strong>Shares:</strong> The endpoint is a Shares if the <strong>host</strong> is an URL like{" "}
            <strong>https://aspera-1.kanopy.com/login</strong>. You should see clearly the "Shares" logo and be
            redirected to a /login page. Please provide <strong>Host</strong> without the end of "/login".
            <br />
            <br />- <strong>Faspex:</strong> The endpoint is a Faspex if the <strong>host</strong> is an URL like{" "}
            <strong>https://aspera03.fotokem.com/aspera/faspex</strong>. You should see clearly the "Faspex" logo and be
            redirected to a /aspera/faspex page. Please provide <strong>Host</strong> including the end of
            "/aspera/faspex".
            <br />
            <br />- <strong>Node API:</strong> The endpoint is a Node API if the <strong>host</strong> is an URL like{" "}
            <strong>https://nodeapi.aspera.io or https://ats-aws-eu-west-1.aspera.io/</strong>. You should have a prompt
            on screen asking to you login & password. If you dont' know, that's propbably this and works seems Shares.
            If you are not in the case "Faspex" or "Shares", you are in the "Node API" case.
            <br />
            <br />- <strong>Server 2 Server:</strong> You will never have an HTTP web page access, only credentials like
            login/password and/or an SSH key file to provide.
            <br />
            <br />
            Except "Server 2 Server", the field <strong>Host</strong> must start with http:// or https://. In case of
            "Server 2 Server", the field <strong>Host</strong> must be the IP address or the DNS name of the server
            without http:// or https://.
          </Alert>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Always follow this guide and never use the "Server 2 Server" type if you are not sure.
            <br />
            If you have any access to a web page (http or https) with a login/password, you are not in the "Server 2
            Server" case but web in Shares, Faspex or Node API.
            <br />
            <br />
            To indicate to the system you're in "Faspex" mode, please always provide the <strong>path</strong> with the
            end of "/aspera/faspex".
            <br />
            <br />
            To finish, theses rules are also perfecly the same for automated Identity Profiles XML.
          </Alert>
        </FormSection>
      )}
      {storage.type && storage.type == StorageType.DIVA && (
        <FormSection title={t("form:section.title.divaSettings")} status="todo" sx={{ mb: 2, mt: 3 }}>
          <Grid container spacing={3} flexDirection={"row"}>
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink htmlFor="port">
                  {t("common:port")}
                </InputLabel>
                <OutlinedInput
                  id="port"
                  type={"number"}
                  value={(storage?.storage as DbStorageDivaInfos)?.port ?? 7100}
                  onChange={(e) => {
                    setStorage({
                      ...storage,
                      storage: {
                        ...(storage.storage ?? ({} as DbStorageDivaInfos)),
                        port: parseInt(e.target.value),
                      },
                    });
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink htmlFor="host">
                  {t("common:host")}
                </InputLabel>
                <OutlinedInput
                  id="host"
                  value={(storage.storage as DbStorageDivaInfos)?.host ?? ""}
                  onChange={(e) => {
                    setStorage({
                      ...storage,
                      storage: {
                        ...(storage.storage ?? ({} as DbStorageDivaInfos)),
                        host: e.target.value,
                      },
                    });
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink htmlFor="user">
                  {t("common:user")}
                </InputLabel>
                <OutlinedInput
                  id="user"
                  value={(storage.storage as DbStorageDivaInfos)?.user ?? ""}
                  onChange={(e) => {
                    setStorage({
                      ...storage,
                      storage: {
                        ...(storage.storage ?? ({} as DbStorageDivaInfos)),
                        user: e.target.value,
                      },
                    });
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink htmlFor="password">
                  {t("common:password")}
                </InputLabel>
                <OutlinedInput
                  id="password"
                  value={(storage.storage as DbStorageDivaInfos)?.password ?? ""}
                  onChange={(e) => {
                    setStorage({
                      ...storage,
                      storage: {
                        ...(storage.storage ?? ({} as DbStorageDivaInfos)),
                        password: e.target.value,
                      },
                    });
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink htmlFor="category">
                  {t("common:category")}
                </InputLabel>
                <OutlinedInput
                  id="category"
                  value={(storage.storage as DbStorageDivaInfos)?.category ?? ""}
                  onChange={(e) => {
                    setStorage({
                      ...storage,
                      storage: {
                        ...((storage.storage as DbStorageDivaInfos) ?? ({} as DbStorageDivaInfos)),
                        category: e.target.value,
                      },
                    });
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink htmlFor="archiveDestinationMediaName">
                  {t("field:label.archiveDestinationMediaName")}
                </InputLabel>
                <OutlinedInput
                  id="archiveDestinationMediaName"
                  value={(storage.storage as DbStorageDivaInfos)?.archiveDestinationMediaName ?? ""}
                  onChange={(e) => {
                    setStorage({
                      ...storage,
                      storage: {
                        ...((storage.storage as DbStorageDivaInfos) ?? ({} as DbStorageDivaInfos)),
                        archiveDestinationMediaName: e.target.value,
                      },
                    });
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item md={6}>
              <InputLabel sx={{ mb: 1 }}>{t("field:label.archiveDestinationMediaType")}</InputLabel>
              <Select
                value={(storage.storage as DbStorageDivaInfos)?.archiveDestinationMediaType ?? ""}
                sx={{ width: "100%" }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                fullWidth
                onChange={(e) => {
                  setStorage({
                    ...storage,
                    storage: {
                      ...((storage.storage as DbStorageDivaInfos) ?? ({} as DbStorageDivaInfos)),
                      archiveDestinationMediaType: e.target.value ? StorageDivaMedia[e.target.value] : null,
                    },
                  });
                }}
              >
                {Object.keys(StorageDivaMedia).map((type) => (
                  <MenuItem value={type} key={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Divider sx={{ m: 2 }} />
            <Grid item md={12}>
              <DivaRestoreDestination
                listOfAllStorages={listOfAllStorages}
                pushToList={(element) => {
                  setStorage({
                    ...storage,
                    storage: {
                      ...((storage.storage as DbStorageDivaInfos) ?? ({} as DbStorageDivaInfos)),
                      restoreDestinations: [
                        element,
                        ...((storage.storage as DbStorageDivaInfos)?.restoreDestinations ?? []),
                      ],
                    },
                  });
                }}
              />
            </Grid>
            <Grid item md={6}>
              {(storage.storage as DbStorageDivaInfos)?.restoreDestinations?.map((x) => (
                <Chip
                  label={`#${x.storageId} - ${x.destinationName}`}
                  onDelete={() =>
                    setStorage({
                      ...storage,
                      storage: {
                        ...((storage.storage as DbStorageDivaInfos) ?? ({} as DbStorageDivaInfos)),
                        restoreDestinations: [
                          ...((storage.storage as DbStorageDivaInfos)?.restoreDestinations?.filter((y) => y !== x) ??
                            []),
                        ],
                      },
                    })
                  }
                />
              ))}
            </Grid>
          </Grid>
        </FormSection>
      )}
      {storage.type && storage.type == StorageType.FTP && (
        <FormSection title={t("form:section.title.ftpSettings")} status="todo" sx={{ mb: 2, mt: 3 }}>
          <Grid container spacing={3} flexDirection={"row"}>
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink htmlFor="port">
                  {t("common:port")}
                </InputLabel>
                <OutlinedInput
                  id="port"
                  type={"number"}
                  value={(storage?.storage as DbStorageFtpInfos)?.port ?? 21}
                  onChange={(e) => {
                    setStorage({
                      ...storage,
                      storage: {
                        ...(storage.storage ?? ({} as DbStorageFtpInfos)),
                        port: parseInt(e.target.value),
                      },
                    });
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink htmlFor="host">
                  {t("common:host")}
                </InputLabel>
                <OutlinedInput
                  id="host"
                  value={(storage.storage as DbStorageFtpInfos)?.host ?? ""}
                  onChange={(e) => {
                    setStorage({
                      ...storage,
                      storage: {
                        ...(storage.storage ?? ({} as DbStorageFtpInfos)),
                        host: e.target.value,
                      },
                    });
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink htmlFor="user">
                  {t("common:user")}
                </InputLabel>
                <OutlinedInput
                  id="user"
                  value={(storage.storage as DbStorageFtpInfos)?.user ?? ""}
                  onChange={(e) => {
                    setStorage({
                      ...storage,
                      storage: {
                        ...(storage.storage ?? ({} as DbStorageFtpInfos)),
                        user: e.target.value,
                      },
                    });
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink htmlFor="password">
                  {t("common:password")}
                </InputLabel>
                <OutlinedInput
                  id="password"
                  value={(storage.storage as DbStorageFtpInfos)?.password ?? ""}
                  onChange={(e) => {
                    setStorage({
                      ...storage,
                      storage: {
                        ...(storage.storage ?? ({} as DbStorageFtpInfos)),
                        password: e.target.value,
                      },
                    });
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink htmlFor="path">
                  {t("common:path")}
                </InputLabel>
                <OutlinedInput
                  id="path"
                  value={(storage.storage as DbStorageFtpInfos)?.path ?? ""}
                  onChange={(e) => {
                    setStorage({
                      ...storage,
                      storage: {
                        ...((storage.storage ?? {}) as DbStorageFtpInfos),
                        path: e.target.value,
                      },
                    });
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink htmlFor="privateSshKeyFilePath">
                  {t("field:label.privateSshKeyFilePath")}
                </InputLabel>
                <OutlinedInput
                  id="privateSshKeyFilePath"
                  value={(storage.storage as DbStorageFtpInfos)?.privateSshKeyFilePath ?? ""}
                  onChange={(e) => {
                    setStorage({
                      ...storage,
                      storage: {
                        ...((storage.storage ?? {}) as DbStorageFtpInfos),
                        privateSshKeyFilePath: e.target.value,
                      },
                    });
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item md={6}>
              <InputLabel sx={{ mb: 1 }}>{t("common:protocol")}</InputLabel>
              <Select
                value={(storage.storage as DbStorageFtpInfos)?.protocol ?? StorageFtpProtocol.FTP}
                sx={{ width: "100%" }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                fullWidth
                onChange={(e) => {
                  setStorage({
                    ...storage,
                    storage: {
                      ...((storage.storage ?? {}) as DbStorageFtpInfos),
                      protocol: StorageFtpProtocol[e.target.value],
                    },
                  });
                }}
              >
                {Object.keys(StorageFtpProtocol).map((pt) => (
                  <MenuItem value={pt} key={pt}>
                    {pt}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          </Grid>
        </FormSection>
      )}
      {storage.type && storage.type == StorageType.SNSM && (
        <FormSection title={t("form:section.title.snsmSettings")} status="todo" sx={{ mb: 2, mt: 3 }}>
          <Grid container spacing={3} flexDirection={"row"}>
            <Grid item md={6}>
              <InputLabel sx={{ mb: 1 }}>{t("field:label.apiMode")}</InputLabel>
              <Select
                value={(storage.storage as DbStorageSnsmInfos)?.apiMode ?? ""}
                sx={{ width: "100%" }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                fullWidth
                onChange={(e) => {
                  setStorage({
                    ...storage,
                    storage: {
                      ...((storage.storage ?? {}) as DbStorageSnsmInfos),
                      apiMode: e.target.value ? StorageSnsmApiMode[e.target.value] : null,
                    },
                  });
                }}
              >
                {Object.keys(StorageSnsmApiMode).map((type) => (
                  <MenuItem value={type} key={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item md={6}>
              <InputLabel sx={{ mb: 1 }}>{t("common:version")}</InputLabel>
              <Select
                value={(storage.storage as DbStorageSnsmInfos)?.version ?? ""}
                sx={{ width: "100%" }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                fullWidth
                onChange={(e) => {
                  setStorage({
                    ...storage,
                    storage: {
                      ...((storage.storage ?? {}) as DbStorageSnsmInfos),
                      version: e.target.value ? StorageSnsmVersion[e.target.value] : null,
                    },
                  });
                }}
              >
                {Object.keys(StorageSnsmVersion).map((type) => (
                  <MenuItem value={type} key={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink htmlFor="apiBaseUrl">
                  {t("field:label.apiBaseUrl")}
                </InputLabel>
                <OutlinedInput
                  id="apiBaseUrl"
                  value={(storage.storage as DbStorageSnsmInfos)?.apiBaseUrl ?? ""}
                  onChange={(e) => {
                    setStorage({
                      ...storage,
                      storage: {
                        ...((storage.storage ?? {}) as DbStorageSnsmInfos),
                        apiBaseUrl: e.target.value,
                      },
                    });
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink htmlFor="user">
                  {t("common:user")}
                </InputLabel>
                <OutlinedInput
                  id="user"
                  value={(storage.storage as DbStorageSnsmInfos)?.user ?? ""}
                  onChange={(e) => {
                    setStorage({
                      ...storage,
                      storage: {
                        ...(storage.storage ?? ({} as DbStorageSnsmInfos)),
                        user: e.target.value,
                      },
                    });
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink htmlFor="password">
                  {t("common:password")}
                </InputLabel>
                <OutlinedInput
                  id="password"
                  value={(storage.storage as DbStorageSnsmInfos)?.password ?? ""}
                  onChange={(e) => {
                    setStorage({
                      ...storage,
                      storage: {
                        ...(storage.storage ?? ({} as DbStorageSnsmInfos)),
                        password: e.target.value,
                      },
                    });
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink htmlFor="libraryName">
                  {t("field:label.libraryName")}
                </InputLabel>
                <OutlinedInput
                  id="libraryName"
                  value={(storage.storage as DbStorageSnsmInfos)?.libraryName ?? ""}
                  onChange={(e) => {
                    setStorage({
                      ...storage,
                      storage: {
                        ...((storage.storage as DbStorageSnsmInfos) ?? ({} as DbStorageSnsmInfos)),
                        libraryName: e.target.value,
                      },
                    });
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink htmlFor="intervalMinutes">
                  {t("field:label.intervalInMinutes")}
                </InputLabel>
                <OutlinedInput
                  id="intervalMinutes"
                  type={"number"}
                  value={(storage.storage as DbStorageSnsmInfos)?.syncSettings?.intervalMinutes ?? 5}
                  onChange={(e) => {
                    setStorage({
                      ...storage,
                      storage: {
                        ...((storage.storage as DbStorageSnsmInfos) ?? ({} as DbStorageSnsmInfos)),
                        syncSettings: {
                          ...((storage.storage as DbStorageSnsmInfos)?.syncSettings ??
                            ({} as DbStorageSnsmSyncSettingsInfos)),
                          intervalMinutes: parseInt(e.target.value),
                        },
                      },
                    });
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink htmlFor="batchObjectSize">
                  {t("field:label.batchObjectSize")}
                </InputLabel>
                <OutlinedInput
                  id="batchObjectSize"
                  type={"number"}
                  value={(storage.storage as DbStorageSnsmInfos)?.syncSettings?.batchObjectSize ?? 200}
                  onChange={(e) => {
                    setStorage({
                      ...storage,
                      storage: {
                        ...((storage.storage as DbStorageSnsmInfos) ?? ({} as DbStorageSnsmInfos)),
                        syncSettings: {
                          ...((storage.storage as DbStorageSnsmInfos)?.syncSettings ??
                            ({} as DbStorageSnsmSyncSettingsInfos)),
                          batchObjectSize: parseInt(e.target.value),
                        },
                      },
                    });
                  }}
                />
              </FormControl>
            </Grid>
            <SettingPathField
              linuxPath={(storage.storage?.path as SettingPath | null)?.linux}
              windowsPath={(storage.storage?.path as SettingPath | null)?.windows}
              onChange={(e) => {
                setStorage({
                  ...storage,
                  storage: {
                    ...(storage.storage ?? ({} as DbStorageSnsmInfos)),
                    path: e,
                  },
                });
              }}
            />
            <Grid item md={12}>
              <Paper sx={{ backgroundImage: "none", p: 2 }}>
                <Typography mb={2} variant="h4">
                  {t("modal:modalStorageEdit.body.pathAliases")}
                </Typography>
                <PathAliasField
                  pushToList={(element) => {
                    setStorage({
                      ...storage,
                      storage: {
                        ...((storage.storage as DbStorageSnsmInfos) ?? ({} as DbStorageSnsmInfos)),
                        pathAliases: [element, ...((storage.storage as DbStorageSnsmInfos)?.pathAliases ?? [])],
                      },
                    });
                  }}
                />
                <Grid item md={12}>
                  {(storage.storage as DbStorageSnsmInfos)?.pathAliases?.map((x) => (
                    <Chip
                      sx={{ ml: 1, mb: 0.3 }}
                      label={t("chip:label.storagePath", {
                        type: x.type,
                        linuxPath: x.path?.linux,
                        windowPath: x.path?.windows,
                      })}
                      onDelete={() =>
                        setStorage({
                          ...storage,
                          storage: {
                            ...((storage.storage as DbStorageSnsmInfos) ?? ({} as DbStorageSnsmInfos)),
                            pathAliases: [
                              ...((storage.storage as DbStorageSnsmInfos)?.pathAliases?.filter((y) => y !== x) ?? []),
                            ],
                          },
                        })
                      }
                    />
                  ))}
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </FormSection>
      )}
      {storage.type && storage.type == StorageType.AMAZONS3 && (
        <FormSection title={t("form:section.title.amazoneS3Settings")} status="todo" sx={{ mb: 2, mt: 3 }}>
          <Grid container spacing={3} flexDirection={"row"}>
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink htmlFor="url">
                  Url
                </InputLabel>
                <OutlinedInput
                  id="url"
                  value={(storage.storage as DbStorageAmazonS3Infos)?.url ?? ""}
                  onChange={(e) => {
                    setStorage({
                      ...storage,
                      storage: {
                        ...((storage.storage as DbStorageAmazonS3Infos) ?? ({} as DbStorageAmazonS3Infos)),
                        url: e.target.value,
                      },
                    });
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink htmlFor="bucketName">
                  {t("field:label.bucketName")}
                </InputLabel>
                <OutlinedInput
                  id="bucketName"
                  value={(storage.storage as DbStorageAmazonS3Infos)?.bucketName ?? ""}
                  onChange={(e) => {
                    setStorage({
                      ...storage,
                      storage: {
                        ...((storage.storage as DbStorageAmazonS3Infos) ?? ({} as DbStorageAmazonS3Infos)),
                        bucketName: e.target.value,
                      },
                    });
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink htmlFor="accessKey">
                  {t("field:label.assessKey")}
                </InputLabel>
                <OutlinedInput
                  id="accessKey"
                  value={(storage.storage as DbStorageAmazonS3Infos)?.accessKey ?? ""}
                  onChange={(e) => {
                    setStorage({
                      ...storage,
                      storage: {
                        ...((storage.storage as DbStorageAmazonS3Infos) ?? ({} as DbStorageAmazonS3Infos)),
                        accessKey: e.target.value,
                      },
                    });
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink htmlFor="secretKey">
                  {t("field:label.secretKey")}
                </InputLabel>
                <OutlinedInput
                  id="secretKey"
                  value={(storage.storage as DbStorageAmazonS3Infos)?.secretKey ?? ""}
                  onChange={(e) => {
                    setStorage({
                      ...storage,
                      storage: {
                        ...((storage.storage as DbStorageAmazonS3Infos) ?? ({} as DbStorageAmazonS3Infos)),
                        secretKey: e.target.value,
                      },
                    });
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink htmlFor="region">
                  {t("field:label.awsRegion")}
                </InputLabel>
                <OutlinedInput
                  id="region"
                  value={(storage.storage as DbStorageAmazonS3Infos)?.region ?? ""}
                  onChange={(e) => {
                    setStorage({
                      ...storage,
                      storage: {
                        ...((storage.storage as DbStorageAmazonS3Infos) ?? ({} as DbStorageAmazonS3Infos)),
                        region: e.target.value,
                      },
                    });
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink htmlFor="roleArn">
                  {t("field:label.roleArn")}
                </InputLabel>
                <OutlinedInput
                  id="roleArn"
                  value={(storage.storage as DbStorageAmazonS3Infos)?.roleArn ?? ""}
                  onChange={(e) => {
                    setStorage({
                      ...storage,
                      storage: {
                        ...((storage.storage as DbStorageAmazonS3Infos) ?? ({} as DbStorageAmazonS3Infos)),
                        roleArn: e.target.value,
                      },
                    });
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink htmlFor="docRoot">
                  {t("field:label.docRoot")}
                </InputLabel>
                <OutlinedInput
                  id="docRoot"
                  value={(storage.storage as DbStorageAmazonS3Infos)?.docRoot ?? ""}
                  onChange={(e) => {
                    setStorage({
                      ...storage,
                      storage: {
                        ...((storage.storage as DbStorageAmazonS3Infos) ?? ({} as DbStorageAmazonS3Infos)),
                        docRoot: e.target.value,
                      },
                    });
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item md={6}>
              <InputLabel sx={{ mb: 1 }}>{t("field:label.s3Class")}</InputLabel>
              <Select
                value={
                  (storage.storage as DbStorageAmazonS3Infos)?.s3class
                    ? S3StorageClass[(storage.storage as DbStorageAmazonS3Infos).s3class!]
                    : S3StorageClass.Standard
                }
                sx={{ width: "100%" }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                fullWidth
                onChange={(e) => {
                  setStorage({
                    ...storage,
                    storage: {
                      ...((storage.storage as DbStorageAmazonS3Infos) ?? ({} as DbStorageAmazonS3Infos)),
                      s3class: e.target.value ? S3StorageClass[e.target.value] : null,
                    },
                  });
                }}
              >
                {Object.keys(S3StorageClass).map((type) => (
                  <MenuItem value={type} key={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          </Grid>
        </FormSection>
      )}
    </Modal>
  );
});

type DivaRestoreDestinationProps = {
  listOfAllStorages: { id: number; name: string }[];
  pushToList: (destination: DbStorageDivaDestinationMapInfos) => void;
};

const DivaRestoreDestination = (props: DivaRestoreDestinationProps) => {
  const { t } = useTranslation();
  const [destinationName, setDestinationName] = useState<string>("");
  const [storageId, setStorageId] = useState<number | null>(null);

  const onClick = () => {
    props.pushToList({ storageId, destinationName: destinationName! });
    setStorageId(null);
    setDestinationName("");
  };

  return (
    <Stack>
      <Grid container spacing={3} flexDirection={"row"}>
        <Grid item md={6}>
          <InputLabel htmlFor="destinationName" sx={{ mb: 1 }}>
            {t("field:label.destinationName")}
          </InputLabel>
          <OutlinedInput
            id="destinationName"
            value={destinationName}
            fullWidth
            onChange={(e) => {
              setDestinationName(e.target.value);
            }}
          />
        </Grid>
        <Grid item md={6}>
          <InputLabel sx={{ mb: 1 }}>{t("common:storage")}</InputLabel>
          <Select
            value={storageId}
            sx={{ width: "100%" }}
            MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
            fullWidth
            onChange={(e) => {
              setStorageId(
                e.target.value ? (typeof e.target.value == "string" ? parseInt(e.target.value) : e.target.value) : null
              );
            }}
          >
            {props.listOfAllStorages?.map((type) => (
              <MenuItem value={type.id} key={type.name}>
                {type.name} (#{type.id})
              </MenuItem>
            ))}
          </Select>
        </Grid>
      </Grid>
      <Button
        variant="contained"
        color="primary"
        onClick={onClick}
        disabled={storageId === null || destinationName === ""}
        sx={{ mt: 2 }}
      >
        {t("common:add")}
      </Button>
    </Stack>
  );
};

type SettingPathFieldProps = {
  onChange(settingsPath: SettingPath): void;
  linuxPath?: string;
  windowsPath?: string;
};

const SettingPathField = (props: SettingPathFieldProps) => {
  const { t } = useTranslation();
  const [linuxPath, setLinuxPath] = useState<string>(props.linuxPath ?? "");
  const [windowsPath, setWindowsPath] = useState<string>(props.windowsPath ?? "");

  useEffect(() => props.onChange({ linux: linuxPath, windows: windowsPath }), [linuxPath, windowsPath]);

  return (
    <>
      <Grid item md={6}>
        <FormControl sx={{ width: "100%" }}>
          <InputLabel shrink htmlFor="linuxPath">
            {t("field:label.linuxPath")}
          </InputLabel>
          <OutlinedInput
            id="linuxPath"
            value={linuxPath}
            onChange={(e) => {
              setLinuxPath(e.target.value);
            }}
          />
        </FormControl>
      </Grid>
      <Grid item md={6}>
        <FormControl sx={{ width: "100%" }}>
          <InputLabel shrink htmlFor="windowsPath">
            {t("field:label.windowsPath")}
          </InputLabel>
          <OutlinedInput
            id="windowsPath"
            value={windowsPath}
            onChange={(e) => {
              setWindowsPath(e.target.value);
            }}
          />
        </FormControl>
      </Grid>
    </>
  );
};

type PathAliasFieldProps = {
  pushToList: (destination: DbStoragePathAliasInfos) => void;
};

const PathAliasField = (props: PathAliasFieldProps) => {
  const { t } = useTranslation();
  const [type, setType] = useState<StoragePathAliasType>();
  const [path, setPath] = useState<SettingPath>();

  return (
    <>
      <Grid item md={6}>
        <InputLabel sx={{ mb: 1 }}>{t("common:type")}</InputLabel>
        <Select
          value={type}
          sx={{ width: "100%" }}
          MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
          fullWidth
          onChange={(e) => {
            setType(e.target.value ? StoragePathAliasType[e.target.value] : null);
          }}
        >
          {Object.keys(StoragePathAliasType).map((type) => (
            <MenuItem value={type} key={type}>
              {type}
            </MenuItem>
          ))}
        </Select>
      </Grid>
      <SettingPathField onChange={setPath} />
      <Button
        variant="contained"
        color="primary"
        onClick={() => props.pushToList({ type: type!, path: path! })}
        disabled={type === undefined || (path?.linux === "" && path.windows === "")}
        sx={{ mt: 2 }}
      >
        {t("common:add")}
      </Button>
    </>
  );
};

export default ModalStorageEdit;
